import React, { Dispatch, FC, SetStateAction } from 'react';
import { TicketMap } from '@ticketevolution/seatmaps-client';
import { Grid, Skeleton } from '@mui/material';
import { useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import styled from 'styled-components';
import { TicketGroupTEType } from '../../containers/book/book';
import { RangePriceSlider } from './rangePriceSlider';
import { QuantityButton } from './quantityButton';
import { LowPriceButton } from './lowPriceButton';
import { FilterByTag } from './filterByTag';
import { FilterParams } from '../../store/enums';
import { BinocularsFilter } from './binocularsFilter';
import { ClearAllButton } from './clearAllButton';
import { EventBookType } from '../../core/models/events';

const useStyles = makeStyles()((theme) => ({
  sortButtonGroup: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
    [theme.breakpoints.down(600)]: {
      justifyContent: 'space-between',
      gap: 6,
    },
  },
  sliderContainer: {
    width: '324px',
    [theme.breakpoints.down(600)]: {
      width: '100%',
    },
  },
  sortContainer: {
    padding: '40px 0 0 0',
    flexShrink: 0,
    [theme.breakpoints.down(600)]: {
      flexDirection: 'column',
      padding: '8px 0 0 0',
      gap: 6,
    },
  },
  mapSkeletonRoot: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  mapSkeletonFill: {
    position: 'absolute',
    inset: 0,
    transform: 'scale(1)',
    borderRadius: 8,
  },
  mapSkeletonControls: {
    position: 'absolute',
    top: 8,
    left: 8,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    zIndex: 1,
    pointerEvents: 'none',
    [theme.breakpoints.down(600)]: {
      top: 6,
      left: 6,
      gap: 6,
    },
  },
  mapSkeletonPillSm: {
    borderRadius: 16,
    transform: 'scale(1)',
  },
  mapSkeletonPillLg: {
    borderRadius: 16,
    transform: 'scale(1)',
  },
  filtersSkeletonBlock: {
    flexShrink: 0,
    width: '100%',
    paddingTop: 8,
    [theme.breakpoints.down(600)]: {
      paddingTop: 6,
    },
  },
  filtersSkeletonRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    [theme.breakpoints.down(600)]: {
      marginBottom: 4,
    },
  },
  filtersSkeletonChips: {
    display: 'flex',
    gap: 6,
    marginTop: 6,
    [theme.breakpoints.down(600)]: {
      gap: 4,
      marginTop: 4,
      flexWrap: 'wrap',
    },
  },
  title: {
    fontSize: '14px',
    margin: '0 0 20px 0',
  },

  buttonsByTag: {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  buttonsByPrice: {
    display: 'flex',
    gap: 4,
  },
  skeleton: {
    transform: 'scale(1)',
  },
}));

const TESchemaSectorSelector: FC<TESchemaSectorSelectorPropsType> = ({
  ticketGroups = [],
  tEVenueId,
  configurationId,
  setSelectedSections,
  selectedSections,
  event,
  isLoading,
  selectTicketGroupId,
}) => {
  const location = useLocation();
  const { classes } = useStyles();
  // @ts-ignore
  const customWindow: any = window;

  const handleSectionClick = (sections: string[]) => {
    setSelectedSections(sections);
  };

  const showMapSkeleton =
    isLoading || !tEVenueId || !configurationId || !ticketGroups || ticketGroups.length <= 0;

  return (
    <Container>
      <SchemeWrapper>
        {showMapSkeleton && (
          <div className={classes.mapSkeletonRoot}>
            <Skeleton
              className={classes.mapSkeletonFill}
              animation="wave"
              variant="rectangular"
              width="100%"
              height="100%"
            />
            <div className={classes.mapSkeletonControls} aria-hidden>
              <Skeleton
                className={classes.mapSkeletonPillSm}
                animation="wave"
                variant="rectangular"
                width={72}
                height={32}
              />
              <Skeleton
                className={classes.mapSkeletonPillLg}
                animation="wave"
                variant="rectangular"
                width={148}
                height={32}
              />
            </div>
          </div>
        )}
        {!isLoading && tEVenueId && configurationId && ticketGroups && ticketGroups.length > 0 ? (
          <TicketMap
            selectedSections={selectedSections}
            onSelection={handleSectionClick}
            venueId={tEVenueId}
            configurationId={configurationId.toString()}
            // @ts-ignore
            ticketGroups={ticketGroups}
          />
        ) : null}
      </SchemeWrapper>
      {isLoading && (
        <div className={classes.filtersSkeletonBlock}>
          <div className={classes.filtersSkeletonRow}>
            <Skeleton className={classes.skeleton} animation="wave" width="55%" height={14} />
            <Skeleton className={classes.skeleton} animation="wave" width="28%" height={14} />
          </div>
          <Skeleton className={classes.skeleton} animation="wave" variant="rectangular" width="100%" height={6} sx={{ borderRadius: 3 }} />
          <div className={classes.filtersSkeletonChips}>
            <Skeleton className={classes.skeleton} animation="wave" variant="rectangular" width={88} height={30} sx={{ borderRadius: 16 }} />
            <Skeleton className={classes.skeleton} animation="wave" variant="rectangular" width={76} height={30} sx={{ borderRadius: 16 }} />
            <Skeleton className={classes.skeleton} animation="wave" variant="rectangular" width={34} height={34} sx={{ borderRadius: '50%' }} />
          </div>
        </div>
      )}
      {!isLoading && !selectTicketGroupId && (
        <Grid container justifyContent={'space-between'} className={classes.sortContainer}>
          <Grid item className={classes.sliderContainer}>
            <RangePriceSlider priceRange={[event?.minPrice, event?.maxPrice]} currency={event?.currency} />
          </Grid>
          <Grid item className={classes.sortButtonGroup}>
            <Grid item className={classes.buttonsByPrice}>
              <QuantityButton />
              <LowPriceButton />
            </Grid>
            <Grid item className={classes.buttonsByTag}>
              <FilterByTag icon={FilterParams.DisabledPerson} />
              {/*{customWindow.ENVIRONMENT !== 'production' && <BinocularsFilter />}*/}
              {/*<FilterByTag icon={FilterParams.Parking} />*/}
              {location.search && <ClearAllButton />}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default TESchemaSectorSelector;

type TESchemaSectorSelectorPropsType = {
  selectedSections: string[];
  setSelectedSections: Dispatch<SetStateAction<string[]>>;
  ticketGroups: TicketGroupTEType[];
  tEVenueId: string;
  configurationId: number;
  event: EventBookType;
  isLoading: boolean;
  selectTicketGroupId: string;
};

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  background: linear-gradient(180.62deg, rgba(28, 28, 28, 0) -91.79%, rgba(18, 17, 17, 0.41) 157.66%);
  box-shadow: 4px -4px 16px rgb(0 0 0 / 63%), -4px 4px 16px rgba(0, 0, 0, 0.63);
  border-radius: 12px;
  padding: 32px;
  height: 502px;
  position: relative;
  width: 100%;
  color: #fff;

  @media (max-width: 600px) {
    padding: 10px 10px 8px;
    height: 322px;
  }
`;

const SchemeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  color: #000;
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
`;
